import { timeZone } from './date';

export type DateTimeFormatOptions = {
  timeZone?: string;
  showTimeZone?: boolean;
};

const formatters = new Map<string, Intl.DateTimeFormat>();

/** Shared 24-hour display format. Numeric API timestamps use the configured region timezone. */
export const getFormattedDateTime = (
  date: Date | number,
  displayTime = true,
  displaySeconds = false,
  options: DateTimeFormatOptions = {}
): string => {
  // Existing Date callers pass local wall-clock values, such as date-picker selections.
  const zone =
    options.timeZone ?? (typeof date === 'number' ? timeZone.value : undefined);
  const key = JSON.stringify([
    zone,
    displayTime,
    displaySeconds,
    options.showTimeZone,
  ]);
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(undefined, {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: displayTime ? '2-digit' : undefined,
      minute: displayTime ? '2-digit' : undefined,
      second: displayTime && displaySeconds ? '2-digit' : undefined,
      hourCycle: 'h23',
      timeZone: zone,
      timeZoneName: displayTime && options.showTimeZone ? 'short' : undefined,
    });
    if (formatters.size >= 64) formatters.clear();
    formatters.set(key, formatter);
  }
  return formatter.format(date);
};

/**
 * Named display formats beside the numeric default, all 24-hour:
 * - `time`: "06:48"
 * - `short`: "Oct 5, 15:10" (month and day with the time, for dense operational views)
 * - `weekday`: "Tue, Oct 6"
 * - `numeric`: "10/05/26 15:10", as `getFormattedDateTime`
 */
export type OtsDateTimeFormat = 'numeric' | 'time' | 'short' | 'weekday';

const namedFormatOptions: Record<Exclude<OtsDateTimeFormat, 'numeric'>, Intl.DateTimeFormatOptions> = {
  time: { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
  short: { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
  weekday: { weekday: 'short', month: 'short', day: 'numeric' },
};

/**
 * One timestamp in a named format, with the same time zone rule as `getFormattedDateTime`: API timestamps
 * (numbers) in the configured region, `Date` values as local wall-clock time, unless `timeZone` is given.
 */
export const formatOtsDateTime = (
  date: Date | number,
  format: OtsDateTimeFormat,
  options: DateTimeFormatOptions = {}
): string => {
  if (format === 'numeric') return getFormattedDateTime(date, true, false, options);
  const zone = options.timeZone ?? (typeof date === 'number' ? timeZone.value : undefined);
  const key = JSON.stringify(['named', format, zone, options.showTimeZone]);
  let formatter = formatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      ...namedFormatOptions[format],
      timeZone: zone,
      timeZoneName: format !== 'weekday' && options.showTimeZone ? 'short' : undefined,
    });
    if (formatters.size >= 64) formatters.clear();
    formatters.set(key, formatter);
  }
  return formatter.format(date);
};
